<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import type {
  K8sConfig,
  K8sUsageSummary,
  K8sProjectedBill,
  K8sDomain,
  K8sStatus,
  K8sDashboardOverview,
  K8sWorkload,
  K8sPod,
  K8sEvent
} from '@/types/dashboard/kubernetes'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AdminK8sTenantDetail' })

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notifyStore = useNotifyStore()

const accountId = computed(() => Number(route.params.id))
const loading = ref(true)
const actionLoading = ref(false)
const activeTab = ref('config')
const showDeprovisionDialog = ref(false)
const showRegenerateDialog = ref(false)

const config = ref<K8sConfig | null>(null)
const usage = ref<K8sUsageSummary | null>(null)
const projected = ref<K8sProjectedBill | null>(null)
const domains = ref<K8sDomain[]>([])
const status = ref<K8sStatus | null>(null)
const spendingCap = ref<number | null>(null)

const liveLoading = ref(false)
const overview = ref<K8sDashboardOverview | null>(null)
const workloads = ref<K8sWorkload[]>([])
const pods = ref<K8sPod[]>([])
const events = ref<K8sEvent[]>([])

const tabs = [
  { value: 'config', label: 'dashboard.admin.kubernetes.detail.tabs.config' },
  { value: 'live', label: 'dashboard.admin.kubernetes.detail.tabs.live' },
  { value: 'usage', label: 'dashboard.admin.kubernetes.detail.tabs.usage' },
  { value: 'domains', label: 'dashboard.admin.kubernetes.detail.tabs.domains' },
  { value: 'actions', label: 'dashboard.admin.kubernetes.detail.tabs.actions' }
]

async function fetchTenant() {
  loading.value = true
  try {
    const res = await useAxios<null, {
      config: K8sConfig
      usage: K8sUsageSummary | null
      projected: K8sProjectedBill | null
      domains: K8sDomain[]
      status: K8sStatus | null
      spending_cap: number | null
    }>('GET', `/admin/k8s/tenants/${accountId.value}`, true)

    if (res && res.status === STATUS_CODES.OK && res.data) {
      config.value = res.data.config
      usage.value = res.data.usage
      projected.value = res.data.projected
      domains.value = res.data.domains || []
      status.value = res.data.status
      spendingCap.value = res.data.spending_cap
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sTenantDetail' })
  } finally {
    loading.value = false
  }
}

async function fetchLiveData() {
  liveLoading.value = true
  try {
    const [overviewRes, workloadsRes, podsRes, eventsRes] = await Promise.allSettled([
      useAxios<null, K8sDashboardOverview>('GET', `/admin/k8s/tenants/${accountId.value}/dashboard/overview`, true),
      useAxios<null, K8sWorkload[]>('GET', `/admin/k8s/tenants/${accountId.value}/dashboard/workloads`, true),
      useAxios<null, K8sPod[]>('GET', `/admin/k8s/tenants/${accountId.value}/dashboard/pods`, true),
      useAxios<null, K8sEvent[]>('GET', `/admin/k8s/tenants/${accountId.value}/dashboard/events`, true)
    ])

    if (overviewRes.status === 'fulfilled' && overviewRes.value?.data) {
      overview.value = overviewRes.value.data
    }
    if (workloadsRes.status === 'fulfilled' && workloadsRes.value?.data) {
      workloads.value = workloadsRes.value.data as K8sWorkload[]
    }
    if (podsRes.status === 'fulfilled' && podsRes.value?.data) {
      pods.value = podsRes.value.data as K8sPod[]
    }
    if (eventsRes.status === 'fulfilled' && eventsRes.value?.data) {
      events.value = eventsRes.value.data as K8sEvent[]
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sTenantDetail:live' })
  } finally {
    liveLoading.value = false
  }
}

async function deprovision() {
  showDeprovisionDialog.value = false
  actionLoading.value = true
  try {
    const res = await useAxios<null, null>('POST', `/admin/k8s/${accountId.value}/deprovision`, true)
    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.kubernetes.detail.actions.deprovisionSuccess'),
        message: config.value?.namespace || ''
      })
      await fetchTenant()
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sTenantDetail:deprovision' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.admin.kubernetes.detail.actions.error'),
      message: ''
    })
  } finally {
    actionLoading.value = false
  }
}

async function syncUsage() {
  actionLoading.value = true
  try {
    const res = await useAxios<null, null>('POST', '/admin/k8s/usage/sync', true)
    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.kubernetes.detail.actions.syncSuccess'),
        message: ''
      })
      await fetchTenant()
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sTenantDetail:syncUsage' })
  } finally {
    actionLoading.value = false
  }
}

async function regenerateKubeconfig() {
  showRegenerateDialog.value = false
  actionLoading.value = true
  try {
    const res = await useAxios<null, null>('POST', `/admin/k8s/tenants/${accountId.value}/kubeconfig/regenerate`, true)
    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.kubernetes.detail.actions.regenerateSuccess'),
        message: ''
      })
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sTenantDetail:regenerate' })
  } finally {
    actionLoading.value = false
  }
}

async function provision() {
  actionLoading.value = true
  try {
    const res = await useAxios<null, null>('POST', `/admin/k8s/tenants/${accountId.value}/provision`, true)
    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.kubernetes.detail.actions.provisionSuccess'),
        message: config.value?.namespace || ''
      })
      await fetchTenant()
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sTenantDetail:provision' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.admin.kubernetes.detail.actions.error'),
      message: ''
    })
  } finally {
    actionLoading.value = false
  }
}

async function verifyDomain(domain: string) {
  try {
    await useAxios<null, null>('POST', `/admin/k8s/tenants/${accountId.value}/domains/${domain}/verify`, true)
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.admin.kubernetes.detail.domains.verifySuccess'),
      message: domain
    })
    await fetchTenant()
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sTenantDetail:verifyDomain' })
  }
}

async function deleteDomain(domain: string) {
  try {
    await useAxios<null, null>('DELETE', `/admin/k8s/tenants/${accountId.value}/domains/${domain}`, true)
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.admin.kubernetes.detail.domains.deleteSuccess'),
      message: domain
    })
    await fetchTenant()
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminK8sTenantDetail:deleteDomain' })
  }
}

function formatDate(ts: number | null | undefined): string {
  if (!ts) return '-'
  return DateTime.fromSeconds(ts).toFormat('dd.MM.yyyy HH:mm')
}

function formatCurrency(cents: number): string {
  return (cents / 100).toFixed(2) + ' €'
}

function onTabChange(tab: string) {
  activeTab.value = tab
  if (tab === 'live' && !overview.value) {
    fetchLiveData()
  }
}

function getPodStatusClass(podStatus: string): string {
  switch (podStatus.toLowerCase()) {
    case 'running': return 'status-success'
    case 'pending': return 'status-warning'
    case 'failed': case 'crashloopbackoff': return 'status-error'
    case 'succeeded': return 'status-info'
    default: return 'status-grey'
  }
}

function getEventTypeClass(type: string): string {
  return type === 'Warning' ? 'status-warning' : 'status-info'
}

onMounted(() => {
  fetchTenant()
})
</script>

<template>
  <BaseDashboardPage
    :title="config?.namespace || `Tenant #${accountId}`"
    :subtitle="config ? `Account #${accountId}` : ''"
    icon="mdi-kubernetes"
    icon-color="cyan"
  >
    <template #actions>
      <button class="btn-text" @click="router.push({ name: 'dashboardAdminK8sHome' })">
        <v-icon icon="mdi-arrow-left" size="16" />
        {{ $t('dashboard.admin.kubernetes.detail.back') }}
      </button>
    </template>

    <!-- Loading -->
    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <template v-else-if="config">
      <!-- Tabs -->
      <div class="tab-bar">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          class="tab-item"
          :class="{ active: activeTab === tab.value }"
          @click="onTabChange(tab.value)"
        >
          {{ $t(tab.label) }}
        </button>
      </div>

      <!-- Config Tab -->
      <div v-if="activeTab === 'config'">
        <div class="section-card">
          <h3 class="section-title">{{ $t('dashboard.admin.kubernetes.detail.config.title') }}</h3>
          <div class="kv-grid">
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.namespace') }}</span>
              <span class="mono">{{ config.namespace }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.status') }}</span>
              <span class="status-chip" :class="`status-${config.status}`">{{ config.status }}</span>
            </div>
            <div v-if="config.error_message" class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.errorMessage') }}</span>
              <span class="error-text">{{ config.error_message }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.provisionedAt') }}</span>
              <span>{{ formatDate(config.provisioned_at) }}</span>
            </div>
            <div v-if="config.git_repo_url" class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.gitRepo') }}</span>
              <span class="mono">{{ config.git_repo_url }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.gitPath') }}</span>
              <span class="mono">{{ config.git_path }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.gitRevision') }}</span>
              <span class="mono">{{ config.git_revision }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.selfHeal') }}</span>
              <span>{{ config.self_heal ? $t('dashboard.admin.kubernetes.detail.config.yes') : $t('dashboard.admin.kubernetes.detail.config.no') }}</span>
            </div>
            <div v-if="status" class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.syncStatus') }}</span>
              <span>{{ status.sync_status || '-' }}</span>
            </div>
            <div v-if="status" class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.healthStatus') }}</span>
              <span>{{ status.health_status || '-' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.config.spendingCap') }}</span>
              <span>{{ spendingCap ? formatCurrency(spendingCap) : '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Live Tab -->
      <div v-else-if="activeTab === 'live'">
        <div v-if="liveLoading" class="state-box">
          <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
        </div>
        <template v-else>
          <!-- Overview -->
          <div v-if="overview" class="section-card" style="margin-bottom: 24px;">
            <h3 class="section-title">{{ $t('dashboard.admin.kubernetes.detail.live.overview') }}</h3>
            <div class="quota-grid">
              <div v-for="(item, key) in overview.quota" :key="key" class="quota-item">
                <span class="quota-label">{{ key }}</span>
                <span class="quota-value mono">{{ item.used }} / {{ item.hard }}</span>
              </div>
            </div>
          </div>

          <!-- Workloads -->
          <div v-if="workloads.length" class="section-card" style="margin-bottom: 24px;">
            <h3 class="section-title">{{ $t('dashboard.admin.kubernetes.detail.live.workloads') }}</h3>
            <div class="data-table">
              <div class="table-header workload-grid">
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.name') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.kind') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.ready') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.age') }}</span>
              </div>
              <div v-for="w in workloads" :key="w.name" class="table-row workload-grid">
                <span class="col mono">{{ w.name }}</span>
                <span class="col">{{ w.kind }}</span>
                <span class="col">{{ w.ready_replicas }}/{{ w.replicas }}</span>
                <span class="col date-cell">{{ w.age }}</span>
              </div>
            </div>
          </div>

          <!-- Pods -->
          <div v-if="pods.length" class="section-card" style="margin-bottom: 24px;">
            <h3 class="section-title">{{ $t('dashboard.admin.kubernetes.detail.live.pods') }}</h3>
            <div class="data-table">
              <div class="table-header pod-grid">
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.name') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.status') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.restarts') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.age') }}</span>
              </div>
              <div v-for="p in pods" :key="p.name" class="table-row pod-grid">
                <span class="col mono">{{ p.name }}</span>
                <span class="col">
                  <span class="status-chip" :class="getPodStatusClass(p.status)">{{ p.status }}</span>
                </span>
                <span class="col">{{ p.restarts }}</span>
                <span class="col date-cell">{{ p.age }}</span>
              </div>
            </div>
          </div>

          <!-- Events -->
          <div v-if="events.length" class="section-card">
            <h3 class="section-title">{{ $t('dashboard.admin.kubernetes.detail.live.events') }}</h3>
            <div class="data-table">
              <div class="table-header event-grid">
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.type') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.reason') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.object') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.message') }}</span>
                <span class="col">{{ $t('dashboard.admin.kubernetes.detail.live.count') }}</span>
              </div>
              <div v-for="(e, i) in events" :key="i" class="table-row event-grid">
                <span class="col">
                  <span class="status-chip" :class="getEventTypeClass(e.type)">{{ e.type }}</span>
                </span>
                <span class="col">{{ e.reason }}</span>
                <span class="col mono">{{ e.involved_object }}</span>
                <span class="col event-message">{{ e.message }}</span>
                <span class="col">{{ e.count }}</span>
              </div>
            </div>
          </div>

          <div v-if="!overview && !workloads.length && !pods.length && !events.length" class="state-box">
            <v-icon icon="mdi-kubernetes" size="48" class="empty-icon" />
            <p class="empty-title">{{ $t('dashboard.admin.kubernetes.detail.live.empty') }}</p>
          </div>
        </template>
      </div>

      <!-- Usage Tab -->
      <div v-else-if="activeTab === 'usage'">
        <div v-if="usage" class="section-card" style="margin-bottom: 24px;">
          <h3 class="section-title">{{ $t('dashboard.admin.kubernetes.detail.usage.title') }}</h3>
          <div class="kv-grid">
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.cpuCoreHours') }}</span>
              <span class="mono">{{ usage.cpu_core_hours.toFixed(2) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.memoryGibHours') }}</span>
              <span class="mono">{{ usage.memory_gib_hours.toFixed(2) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.storageGib') }}</span>
              <span class="mono">{{ usage.storage_gib.toFixed(2) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.egressGib') }}</span>
              <span class="mono">{{ usage.egress_gib.toFixed(2) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.period') }}</span>
              <span>{{ formatDate(usage.period_start) }} - {{ formatDate(usage.period_end) }}</span>
            </div>
          </div>
        </div>

        <div v-if="projected" class="section-card">
          <h3 class="section-title">{{ $t('dashboard.admin.kubernetes.detail.usage.projectedBill') }}</h3>
          <div class="kv-grid">
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.cpuCents') }}</span>
              <span class="mono">{{ formatCurrency(projected.cpu_cents) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.memoryCents') }}</span>
              <span class="mono">{{ formatCurrency(projected.memory_cents) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.storageCents') }}</span>
              <span class="mono">{{ formatCurrency(projected.storage_cents) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.egressCents') }}</span>
              <span class="mono">{{ formatCurrency(projected.egress_cents) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.baseFee') }}</span>
              <span class="mono">{{ formatCurrency(projected.base_fee_cents) }}</span>
            </div>
            <div class="kv-row total-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.total') }}</span>
              <span class="mono total-value">{{ formatCurrency(projected.total_cents) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">{{ $t('dashboard.admin.kubernetes.detail.usage.daysElapsed') }}</span>
              <span>{{ projected.days_elapsed }} / {{ projected.days_in_period }}</span>
            </div>
          </div>
        </div>

        <div v-if="!usage && !projected" class="state-box">
          <v-icon icon="mdi-chart-bar" size="48" class="empty-icon" />
          <p class="empty-title">{{ $t('dashboard.admin.kubernetes.detail.live.noUsage') }}</p>
        </div>
      </div>

      <!-- Domains Tab -->
      <div v-else-if="activeTab === 'domains'">
        <div class="section-card">
          <h3 class="section-title">{{ $t('dashboard.admin.kubernetes.detail.domains.title') }}</h3>
          <div v-if="domains.length === 0" class="state-box">
            <v-icon icon="mdi-web" size="48" class="empty-icon" />
            <p class="empty-title">{{ $t('dashboard.admin.kubernetes.detail.domains.empty') }}</p>
          </div>
          <div v-else class="data-table">
            <div class="table-header domain-grid">
              <span class="col">{{ $t('dashboard.admin.kubernetes.detail.domains.domain') }}</span>
              <span class="col">{{ $t('dashboard.admin.kubernetes.detail.domains.verified') }}</span>
              <span class="col">{{ $t('dashboard.admin.kubernetes.detail.domains.token') }}</span>
              <span class="col">{{ $t('dashboard.admin.kubernetes.detail.domains.created') }}</span>
              <span class="col"></span>
            </div>
            <div v-for="d in domains" :key="d.id" class="table-row domain-grid">
              <span class="col mono">{{ d.domain }}</span>
              <span class="col">
                <span class="status-chip" :class="d.verified ? 'status-success' : 'status-warning'">
                  {{ d.verified ? $t('dashboard.admin.kubernetes.detail.live.verified') : $t('dashboard.admin.kubernetes.detail.live.pending') }}
                </span>
              </span>
              <span class="col mono token-cell">{{ d.verification_token }}</span>
              <span class="col date-cell">{{ formatDate(new Date(d.created_at).getTime() / 1000) }}</span>
              <span class="col domain-actions">
                <button v-if="!d.verified" class="btn-text btn-sm" @click="verifyDomain(d.domain)">
                  {{ $t('dashboard.admin.kubernetes.detail.domains.verify') }}
                </button>
                <button class="btn-text btn-sm btn-danger" @click="deleteDomain(d.domain)">
                  {{ $t('dashboard.admin.kubernetes.detail.domains.delete') }}
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Tab -->
      <div v-else-if="activeTab === 'actions'">
        <div class="section-card" style="margin-bottom: 24px;">
          <h3 class="section-title">{{ $t('dashboard.admin.kubernetes.detail.actions.title') }}</h3>
          <div class="actions-list">
            <div class="action-item">
              <div class="action-info">
                <span class="action-name">{{ $t('dashboard.admin.kubernetes.detail.actions.syncUsage') }}</span>
                <span class="action-desc">{{ $t('dashboard.admin.kubernetes.detail.actions.syncUsageDesc') }}</span>
              </div>
              <button class="btn-text" :disabled="actionLoading" @click="syncUsage">
                <v-icon icon="mdi-sync" size="16" />
                {{ $t('dashboard.admin.kubernetes.detail.actions.syncUsage') }}
              </button>
            </div>
            <div class="action-item">
              <div class="action-info">
                <span class="action-name">{{ $t('dashboard.admin.kubernetes.detail.actions.regenerateKubeconfig') }}</span>
                <span class="action-desc">{{ $t('dashboard.admin.kubernetes.detail.actions.regenerateDesc') }}</span>
              </div>
              <button class="btn-text" :disabled="actionLoading" @click="showRegenerateDialog = true">
                <v-icon icon="mdi-key-variant" size="16" />
                {{ $t('dashboard.admin.kubernetes.detail.actions.regenerateKubeconfig') }}
              </button>
            </div>
            <div v-if="config.status !== 'active' && config.status !== 'provisioning'" class="action-item">
              <div class="action-info">
                <span class="action-name">{{ $t('dashboard.admin.kubernetes.detail.actions.provision') }}</span>
                <span class="action-desc">{{ $t('dashboard.admin.kubernetes.detail.actions.provisionDesc') }}</span>
              </div>
              <button class="btn-text" :disabled="actionLoading" @click="provision">
                <v-icon icon="mdi-rocket-launch" size="16" />
                {{ $t('dashboard.admin.kubernetes.detail.actions.provision') }}
              </button>
            </div>
            <div class="action-item action-danger">
              <div class="action-info">
                <span class="action-name">{{ $t('dashboard.admin.kubernetes.detail.actions.deprovision') }}</span>
                <span class="action-desc">{{ $t('dashboard.admin.kubernetes.detail.actions.deprovisionDesc') }}</span>
              </div>
              <button class="btn-text btn-danger" :disabled="actionLoading" @click="showDeprovisionDialog = true">
                <v-icon icon="mdi-delete-outline" size="16" />
                {{ $t('dashboard.admin.kubernetes.detail.actions.deprovision') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="state-box">
      <v-icon icon="mdi-kubernetes" size="48" class="empty-icon" />
      <p class="empty-title">{{ $t('dashboard.admin.kubernetes.detail.live.notFound') }}</p>
    </div>

    <!-- Deprovision Dialog -->
    <v-dialog v-model="showDeprovisionDialog" max-width="440">
      <div class="modal">
        <div class="modal-header">
          <v-icon icon="mdi-alert-circle" size="24" color="#ef5350" />
          <span class="modal-title">{{ $t('dashboard.admin.kubernetes.detail.actions.deprovision') }}</span>
        </div>
        <div class="modal-content">
          <p>{{ $t('dashboard.admin.kubernetes.detail.actions.deprovisionConfirm') }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-text" @click="showDeprovisionDialog = false">{{ $t('common.cancel') }}</button>
          <button class="btn-text btn-danger" @click="deprovision">{{ $t('dashboard.admin.kubernetes.detail.actions.deprovision') }}</button>
        </div>
      </div>
    </v-dialog>

    <!-- Regenerate Dialog -->
    <v-dialog v-model="showRegenerateDialog" max-width="440">
      <div class="modal">
        <div class="modal-header">
          <v-icon icon="mdi-key-variant" size="24" color="#ffb74d" />
          <span class="modal-title">{{ $t('dashboard.admin.kubernetes.detail.actions.regenerateKubeconfig') }}</span>
        </div>
        <div class="modal-content">
          <p>{{ $t('dashboard.admin.kubernetes.detail.actions.regenerateConfirm') }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-text" @click="showRegenerateDialog = false">{{ $t('common.cancel') }}</button>
          <button class="btn-text" @click="regenerateKubeconfig">{{ $t('dashboard.admin.kubernetes.detail.actions.regenerateKubeconfig') }}</button>
        </div>
      </div>
    </v-dialog>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.btn-text {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-danger {
  background: rgba(244, 67, 54, 0.08);
  border-color: rgba(244, 67, 54, 0.2);
  color: #ef5350;

  &:hover {
    background: rgba(244, 67, 54, 0.15);
  }
}

.btn-sm {
  padding: 4px 8px;
  font-size: 0.78rem;
}

.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  margin-bottom: 24px;
  overflow-x: auto;
}

.tab-item {
  padding: 10px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: rgba(200, 218, 255, 0.8);
    background: rgba(120, 163, 255, 0.04);
  }

  &.active {
    color: #00B0FF;
    border-bottom-color: #00B0FF;
  }
}

.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
}

.kv-grid {
  display: flex;
  flex-direction: column;
}

.kv-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
}

.kv-label {
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.85rem;
}

.total-row {
  border-top: 1px solid rgba(120, 163, 255, 0.15);
  margin-top: 4px;
  padding-top: 14px;
}

.total-value {
  font-weight: 600;
  color: #00B0FF;
}

.error-text {
  color: #ef5350;
  font-size: 0.85rem;
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  font-weight: 500;
}

.data-table {
  overflow-x: auto;
}

.table-header,
.table-row {
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }
}

.workload-grid {
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr;
  min-width: 640px;
}

.pod-grid {
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr;
  min-width: 640px;
}

.event-grid {
  display: grid;
  grid-template-columns: 80px 1fr 1.5fr 2fr 60px;
  min-width: 780px;
}

.domain-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1fr 120px;
  min-width: 780px;
}

.event-message {
  font-size: 0.82rem;
  color: rgba(200, 218, 255, 0.7);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.token-cell {
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.5);
  overflow: hidden;
  text-overflow: ellipsis;
}

.domain-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  white-space: nowrap;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-warning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.status-error {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #ef5350;
}

.status-info {
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.3);
  color: #4fc3f7;
}

.status-grey {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.5);
}

.status-active {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-provisioning,
.status-deprovisioning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.status-failed {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #ef5350;
}

.status-deprovisioned {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.5);
}

.quota-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.quota-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: rgba(120, 163, 255, 0.04);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
}

.quota-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
}

.quota-value {
  font-size: 0.9rem;
}

.actions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.action-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(120, 163, 255, 0.04);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
  gap: 16px;

  &.action-danger {
    background: rgba(244, 67, 54, 0.04);
    border-color: rgba(244, 67, 54, 0.1);
  }
}

.action-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.action-desc {
  font-size: 0.82rem;
  color: rgba(200, 218, 255, 0.5);
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  color: rgba(200, 218, 255, 0.25);
  margin-bottom: 12px;
}

.empty-title {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  margin: 0;
}

.modal {
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.modal-content {
  padding: 24px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.9rem;

  p {
    margin: 0;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

@media (max-width: 768px) {
  .kv-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
